import { useEffect, useMemo, useState } from "react";

import { UserFormPermissions, UserGeneralPermissions } from "@simplyk/common";
import { useController, useForm } from "react-hook-form";

import { useCurrentUserContext } from "../../../../../contexts/CurrentUserContext";
import { useLocaleContext } from "../../../../../contexts/LocaleContext";
import { getFormLabel } from "../../../../../helpers/formLabel";
import { trpc } from "../../../../../helpers/trpc";
import { useTranslate } from "../../../../../hooks/useTranslate";
import { useResendCode } from "../../../../SMSCodeStep/useResendCode";
import { UserData } from "../types";

export type UseUserPermissionStateProps = {
  isAllCampaignsDisabled: boolean;
  defaultFormPermissions: Record<UserFormPermissions, boolean>;
  defaultDesabledFormPermissions: Record<UserFormPermissions, boolean>;
  defaultGeneralPermissions: Record<UserGeneralPermissions, boolean>;
  defaultDesabledGeneralPermissions: Record<UserGeneralPermissions, boolean>;
  defaultNotifications: boolean;
  defaultDesabledNotifications: boolean;
  defaultUserData?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  defaultSelectedCampaignIds?: string[];
  onSubmitCallback: (data: {
    data: UserData;
    selectedCampaigns: { label: string; value: string }[];
    code?: string;
  }) => Promise<{ requiresSmsCode?: boolean } | void>;
};

export const useUserPermissionState = ({
  isAllCampaignsDisabled,
  defaultFormPermissions,
  defaultDesabledFormPermissions,
  defaultGeneralPermissions,
  defaultDesabledGeneralPermissions,
  defaultNotifications,
  defaultDesabledNotifications,
  defaultUserData,
  defaultSelectedCampaignIds,
  onSubmitCallback,
}: UseUserPermissionStateProps) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { currentUser } = useCurrentUserContext();

  const { data: formsAsMultiselectValues } = trpc.organizationFormsAsMultiselectValues.useQuery();

  const campaignListOptions = useMemo(
    () =>
      formsAsMultiselectValues?.map((form) => ({
        label: getFormLabel(form, locale),
        value: form.value,
      })) || [],
    [formsAsMultiselectValues, locale]
  );
  const campaignScopeOptions = isAllCampaignsDisabled
    ? [{ label: t("dashboard", "usersManagement.permissions.autoComplete.specific"), value: "specific" }]
    : [
        { label: t("dashboard", "usersManagement.permissions.autoComplete.all"), value: "all" },
        { label: t("dashboard", "usersManagement.permissions.autoComplete.specific"), value: "specific" },
      ];

  const [selectedCampaigns, setSelectedCampaigns] = useState<{ label: string; value: string }[]>([]);

  const hasCampaigns = selectedCampaigns.length > 0;

  const [selectedCampaignScope, setSelectedCampaignScope] = useState<{ label: string; value: string } | undefined>(
    hasCampaigns ? campaignScopeOptions[1] : campaignScopeOptions[0]
  );

  // Initialize selected campaigns from defaults
  useEffect(() => {
    if (defaultSelectedCampaignIds && campaignListOptions.length > 0) {
      const defaultSelectedCampaigns = campaignListOptions.filter((campaign) =>
        defaultSelectedCampaignIds.includes(campaign.value)
      );
      setSelectedCampaigns(defaultSelectedCampaigns);
    }
  }, [defaultSelectedCampaignIds, campaignListOptions]);

  // Form initialization
  const form = useForm<UserData>({
    defaultValues: {
      firstName: defaultUserData?.firstName || "",
      lastName: defaultUserData?.lastName || "",
      email: defaultUserData?.email || "",
      formPermissions: defaultFormPermissions,
      generalPermissions: defaultGeneralPermissions,
      notifications: defaultNotifications,
    },
    shouldUnregister: true,
  });

  // Form permissions controllers
  const editFormController = useController({
    name: `formPermissions.${UserFormPermissions.EditForm}`,
    control: form.control,
    disabled: defaultDesabledFormPermissions[UserFormPermissions.EditForm],
  });
  const managePaymentsController = useController({
    name: `formPermissions.${UserFormPermissions.ManagePayments}`,
    control: form.control,
    disabled: defaultDesabledFormPermissions[UserFormPermissions.ManagePayments],
  });
  const receiptController = useController({
    name: `formPermissions.${UserFormPermissions.Receipt}`,
    control: form.control,
    disabled: defaultDesabledFormPermissions[UserFormPermissions.Receipt],
  });

  // General permissions controllers
  const contactsController = useController({
    name: `generalPermissions.${UserGeneralPermissions.Contacts}`,
    control: form.control,
    disabled: defaultDesabledGeneralPermissions[UserGeneralPermissions.Contacts],
  });
  const bankController = useController({
    name: `generalPermissions.${UserGeneralPermissions.Bank}`,
    control: form.control,
    disabled: defaultDesabledGeneralPermissions[UserGeneralPermissions.Bank],
  });
  const userManagementController = useController({
    name: `generalPermissions.${UserGeneralPermissions.UserManagement}`,
    control: form.control,
    disabled: defaultDesabledGeneralPermissions[UserGeneralPermissions.UserManagement],
  });
  const tapToPayController = useController({
    name: `generalPermissions.${UserGeneralPermissions.TapToPay}`,
    control: form.control,
    disabled: defaultDesabledGeneralPermissions[UserGeneralPermissions.TapToPay],
  });
  const notificationController = useController({
    name: "notifications",
    control: form.control,
    disabled: defaultDesabledNotifications,
  });

  // MFA state
  const [isMFAStep, setIsMFAStep] = useState(false);
  const [code, setCode] = useState("");

  // Resend code hook
  const {
    isResendButtonDisabled,
    resendButtonCountdownText,
    resendCode,
    isLoadingSendSms: isLoadingResendSms,
  } = useResendCode({});

  // Submit handler
  const onSubmit = form.handleSubmit(async (data) => {
    const result = await onSubmitCallback({ data, selectedCampaigns, code: isMFAStep ? code : undefined });
    if (result && result.requiresSmsCode) {
      setIsMFAStep(true);
    }
  });

  const isDisabledSubmitCodeButton = !code || code.length !== 6;

  return {
    form,
    selectedCampaigns,
    setSelectedCampaigns,
    campaignListOptions,
    formPermissionsControllers: {
      editForm: editFormController,
      managePayments: managePaymentsController,
      receipt: receiptController,
    },
    generalPermissionsControllers: {
      contacts: contactsController,
      bank: bankController,
      userManagement: userManagementController,
      tapToPay: tapToPayController,
    },
    notificationsControllers: {
      notification: notificationController,
    },
    isMFAStep,
    code,
    setCode,
    onSubmit,
    maskedPhoneNumber: currentUser?.maskedPhoneNumber,
    mfaHelpers: {
      isResendButtonDisabled,
      resendButtonCountdownText,
      resendCode,
      isLoadingResendSms,
      isDisabledSubmitCodeButton,
    },
    campaignScopeOptions,
    selectedCampaignScope,
    setSelectedCampaignScope,
  };
};

export type UseUserPermissionStateReturn = ReturnType<typeof useUserPermissionState>;
